// コネクタ（接続矢印）の作成 UI。
//
// 状態は canvas-store の connectionDraft で管理する。
// 1. 図形を単一選択中: 4 辺の少し外に接続開始ドットを表示する。ホバーで矢印アイコンへ拡大。
// 2. ドットを pointerdown: 接続モード開始。ポインタへゴースト線を引く（ドラッグ / クリック両対応）。
// 3. 接続先候補（図形の AABB 内）に入ると、その図形へ吸着して 4 辺ドットを表示し、最寄り辺へスナップ。
// 4. 接続先で pointerup（ドラッグ）/ もう一度クリック（クリック操作）で確定し、コネクタを作成する。
//    接続先が無ければ何も作らずキャンセルする。Esc でもキャンセル。

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CANVAS_HEIGHT_PX,
  CANVAS_WIDTH_PX,
  type CanvasObject,
  type ConnectorSide,
} from "@refinement-canvas/domain";
import { ArrowOutwardIconData } from "@meiden-connect/material-symbols";
import { Icon } from "@meiden-connect/ui";
import { useCanvasStore, type ConnectionDraftState } from "./store/canvas-store";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasOpDispatcherContext } from "./dispatcher-context";
import { screenToStage } from "../../lib/canvas-coordinates";
import {
  arrowHeadPoints,
  buildRoundedPath,
  computeConnectorRoute,
  nearestSide,
  sideAnchor,
  sideDirection,
  type ConnectorFrame,
  type ConnectorPoint,
} from "../../lib/connector-geometry";

// 接続開始ドットを辺の外側へ離す距離（ステージ座標 px）。
const DOT_OFFSET_PX = 18;
// クリックとドラッグを区別する移動量しきい値（クライアント px）。
const CLICK_THRESHOLD_PX = 4;
// 接続先 AABB の判定を少しだけ広げる余白（ステージ座標 px）。
const TARGET_HIT_PAD_PX = 6;
const ALL_SIDES: readonly ConnectorSide[] = ["top", "right", "bottom", "left"];
const ARROW_SIZE = 12;
const CORNER_RADIUS = 10;

function frameOf(object: CanvasObject): ConnectorFrame {
  return { x: object.x, y: object.y, w: object.w, h: object.h };
}

// コネクタの接続元 / 接続先になりうるオブジェクトか（ペンストローク・コネクタ・ロックは対象外）。
function isConnectableObject(object: CanvasObject | undefined): object is CanvasObject {
  return (
    object !== undefined &&
    !object.locked &&
    object.type !== "pen-stroke" &&
    object.type !== "connector"
  );
}

// 指定ステージ点を含む最前面の接続先オブジェクトと、その最寄り辺を返す。見つからなければ null。
function findConnectTarget(
  fromObjectId: string,
  point: ConnectorPoint
): { objectId: string; side: ConnectorSide } | null {
  const objects = useCanvasDocStore.getState().objects;
  let best: CanvasObject | null = null;
  for (const object of Object.values(objects)) {
    if (object.id === fromObjectId) continue;
    if (object.type === "pen-stroke" || object.type === "connector") continue;
    if (object.locked) continue;
    const within =
      point.x >= object.x - TARGET_HIT_PAD_PX &&
      point.x <= object.x + object.w + TARGET_HIT_PAD_PX &&
      point.y >= object.y - TARGET_HIT_PAD_PX &&
      point.y <= object.y + object.h + TARGET_HIT_PAD_PX;
    if (!within) continue;
    if (!best || object.zOrder > best.zOrder) best = object;
  }
  if (!best) return null;
  return { objectId: best.id, side: nearestSide(frameOf(best), point) };
}

export function CanvasConnectorOverlay(): JSX.Element | null {
  const dispatcher = useCanvasOpDispatcherContext();
  const connectionDraft = useCanvasStore((s) => s.connectionDraft);
  const selectedIds = useCanvasStore((s) => s.selectedObjectIds);
  const tool = useCanvasStore((s) => s.tool);
  const objects = useCanvasDocStore((s) => s.objects);

  // 進行中の接続操作を中断するための関数を保持し、アンマウント時に確実に後始末する
  // （window へ張ったリスナと connectionDraft の取り残しを防ぐ）。
  const activeCancelRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      activeCancelRef.current?.();
      activeCancelRef.current = null;
    };
  }, []);

  // ドット pointerdown → 接続モード開始。ドラッグ / クリックの両操作に対応する状態機械。
  const beginConnection = useCallback(
    (fromObjectId: string, fromSide: ConnectorSide, event: React.PointerEvent): void => {
      event.preventDefault();
      event.stopPropagation();
      const fromObj = useCanvasDocStore.getState().objects[fromObjectId];
      if (!fromObj) return;
      const startClientX = event.clientX;
      const startClientY = event.clientY;
      const anchor = sideAnchor(frameOf(fromObj), fromSide);
      useCanvasStore.getState().setConnectionDraft({
        fromObjectId,
        fromSide,
        pointer: anchor,
        target: null,
      });

      let mode: "dragging" | "armed" = "dragging";

      // 現在のクライアント座標から draft（pointer / target）を更新し、見つかった target を返す。
      const updateAt = (
        clientX: number,
        clientY: number
      ): { objectId: string; side: ConnectorSide } | null => {
        const view = useCanvasStore.getState().view;
        const pointer = screenToStage({ x: clientX, y: clientY }, view);
        const target = findConnectTarget(fromObjectId, pointer);
        useCanvasStore.getState().setConnectionDraft({ fromObjectId, fromSide, pointer, target });
        return target;
      };

      const cleanup = (): void => {
        window.removeEventListener("pointermove", onMove, true);
        window.removeEventListener("pointerup", onUp, true);
        window.removeEventListener("pointerdown", onDown, true);
        window.removeEventListener("pointercancel", onCancel, true);
        window.removeEventListener("keydown", onKey, true);
        activeCancelRef.current = null;
      };
      const finish = (commit: boolean): void => {
        const draft = useCanvasStore.getState().connectionDraft;
        useCanvasStore.getState().setConnectionDraft(null);
        cleanup();
        if (commit && draft?.target) {
          const created = dispatcher.createConnector(
            { objectId: draft.fromObjectId, side: draft.fromSide },
            draft.target
          );
          if (created) useCanvasStore.getState().setSelectedObjectIds([created.id]);
        }
      };
      const onMove = (ev: PointerEvent): void => {
        updateAt(ev.clientX, ev.clientY);
      };
      const onCancel = (): void => {
        finish(false);
      };
      const onUp = (ev: PointerEvent): void => {
        if (mode !== "dragging") return;
        const target = updateAt(ev.clientX, ev.clientY);
        if (target) {
          finish(true);
          return;
        }
        const moved =
          Math.hypot(ev.clientX - startClientX, ev.clientY - startClientY) > CLICK_THRESHOLD_PX;
        if (moved) {
          // ドラッグして空白で離した → キャンセル。
          finish(false);
          return;
        }
        // 移動なしのクリック → armed モードへ。次のクリックで確定 / キャンセルする。
        mode = "armed";
        window.addEventListener("pointerdown", onDown, true);
      };
      const onDown = (ev: PointerEvent): void => {
        // armed モード: クリック位置に接続先があれば確定、無ければキャンセル。
        ev.preventDefault();
        ev.stopPropagation();
        const target = updateAt(ev.clientX, ev.clientY);
        finish(Boolean(target));
      };
      const onKey = (ev: KeyboardEvent): void => {
        if (ev.key === "Escape") finish(false);
      };

      activeCancelRef.current = () => finish(false);
      window.addEventListener("pointermove", onMove, true);
      window.addEventListener("pointerup", onUp, true);
      window.addEventListener("pointercancel", onCancel, true);
      window.addEventListener("keydown", onKey, true);
    },
    [dispatcher]
  );

  if (connectionDraft) {
    return <ConnectionDraftView draft={connectionDraft} objects={objects} />;
  }

  // 接続開始ドットは選択モード（idle）でのみ表示する。付箋 / 図形 / テキスト / スタンプ配置や
  // ペン描画ツールの選択中は、ドット付近のクリックが接続開始へ化けて配置・描画を阻害するため出さない。
  if (tool !== "idle") return null;
  if (selectedIds.length !== 1) return null;
  const selected = objects[selectedIds[0]!];
  if (!isConnectableObject(selected)) return null;

  return (
    <div
      data-testid="canvas-connector-handles"
      style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: Number.MAX_SAFE_INTEGER }}
    >
      {ALL_SIDES.map((side) => {
        const anchor = sideAnchor(frameOf(selected), side);
        const dir = sideDirection(side);
        return (
          <ConnectionStartDot
            key={side}
            side={side}
            x={anchor.x + dir.x * DOT_OFFSET_PX}
            y={anchor.y + dir.y * DOT_OFFSET_PX}
            onStart={(s, e) => beginConnection(selected.id, s, e)}
          />
        );
      })}
    </div>
  );
}

// 接続開始ドット。ホバーで拡大し、外向きの矢印アイコンを表示する。
function ConnectionStartDot({
  x,
  y,
  side,
  onStart,
}: {
  readonly x: number;
  readonly y: number;
  readonly side: ConnectorSide;
  readonly onStart: (side: ConnectorSide, event: React.PointerEvent) => void;
}): JSX.Element {
  const [hover, setHover] = useState(false);
  const size = hover ? 22 : 12;
  return (
    <div
      data-testid={`connector-start-dot-${side}`}
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onPointerDown={(event) => onStart(side, event)}
      title="ドラッグ（またはクリック）して矢印で接続"
      style={{
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        width: `${size}px`,
        height: `${size}px`,
        marginLeft: `${-size / 2}px`,
        marginTop: `${-size / 2}px`,
        borderRadius: "50%",
        background: hover ? "var(--color-accent)" : "var(--color-surface)",
        border: "2px solid var(--color-accent)",
        color: "var(--color-surface)",
        cursor: "crosshair",
        pointerEvents: "auto",
        display: "grid",
        placeItems: "center",
        boxSizing: "border-box",
      }}
    >
      {hover ? <Icon d={ArrowOutwardIconData} /> : null}
    </div>
  );
}

// 接続作成中のゴースト表示。接続先があれば折れ線ルート + 4 辺ドット、無ければ直線（破線）を描く。
function ConnectionDraftView({
  draft,
  objects,
}: {
  readonly draft: ConnectionDraftState;
  readonly objects: Record<string, CanvasObject>;
}): JSX.Element | null {
  const fromObj = objects[draft.fromObjectId];
  if (!fromObj) return null;
  const fromFrame = frameOf(fromObj);
  const anchor = sideAnchor(fromFrame, draft.fromSide);

  const targetObj = draft.target ? objects[draft.target.objectId] : undefined;

  let points: ConnectorPoint[];
  let dashed: boolean;
  if (draft.target && targetObj) {
    points = computeConnectorRoute(fromFrame, draft.fromSide, frameOf(targetObj), draft.target.side);
    dashed = false;
  } else {
    points = [anchor, draft.pointer];
    dashed = true;
  }
  const d = buildRoundedPath(points, CORNER_RADIUS);
  const tip = points[points.length - 1] ?? draft.pointer;
  const beforeTip = points[points.length - 2] ?? anchor;
  const head = arrowHeadPoints(tip, beforeTip, ARROW_SIZE);

  return (
    <svg
      data-testid="canvas-connector-draft"
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: `${CANVAS_WIDTH_PX}px`,
        height: `${CANVAS_HEIGHT_PX}px`,
        overflow: "visible",
        pointerEvents: "none",
        zIndex: Number.MAX_SAFE_INTEGER,
      }}
    >
      <path
        d={d}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={dashed ? "6 6" : undefined}
      />
      {head && (
        <polyline
          points={`${head.left.x},${head.left.y} ${tip.x},${tip.y} ${head.right.x},${head.right.y}`}
          fill="none"
          stroke="var(--color-accent)"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {/* 接続元アンカー。 */}
      <circle cx={anchor.x} cy={anchor.y} r={4} fill="var(--color-accent)" />
      {/* 接続先候補の 4 辺ドット（スナップ辺を強調）。 */}
      {targetObj &&
        ALL_SIDES.map((side) => {
          const sa = sideAnchor(frameOf(targetObj), side);
          const active = draft.target?.side === side;
          return (
            <circle
              key={side}
              cx={sa.x}
              cy={sa.y}
              r={active ? 6 : 4}
              fill={active ? "var(--color-accent)" : "var(--color-surface)"}
              stroke="var(--color-accent)"
              strokeWidth={2}
            />
          );
        })}
    </svg>
  );
}
